import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { io } from 'socket.io-client';
import { useAuth } from '../context/AuthContext';
import { API_BASE, apiFetch, mediaUrl } from '../apiBase';
import { verifyProof } from '../lib/fair';

const CW = 124;
const VIEW = 620;
const LAND = 72;

const oc = (p) => (p >= 30 ? '#8b8b9c' : p >= 15 ? '#5aa9ff' : p >= 6 ? '#a855f7' : p >= 2 ? '#ff544a' : '#F6BB42');
const pc = (p) => (p >= 10 ? p.toFixed(0) + '%' : p.toFixed(p < 1 ? 2 : 1) + '%');

function pick(drops) {
  const total = drops.reduce((s, d) => s + d.odds, 0);
  const r = Math.random() * total;
  let a = 0;
  for (const d of drops) {
    a += d.odds;
    if (r <= a) return d;
  }
  return drops[drops.length - 1];
}
function rarest(drops) {
  return drops.reduce((m, d) => (d.odds < m.odds ? d : m), drops[0]);
}
// Filler pool for the main (non-bonus) reel — rare/special items only ever
// show up as the masked "Special Gem" at the landing slot (see start()) or
// during an actual Diamond Spin bonus reel, never as regular scenery
// scrolling past on an ordinary spin. Falls back to the full pool if a case
// somehow has nothing but rare drops, so the reel never ends up empty.
function commonPool(drops) {
  const common = drops.filter((d) => d.odds > 10);
  return common.length ? common : drops;
}
// Only used to re-key the result card so its entrance animation replays on
// every open — it's not a roll, and nothing is verified against it.
function makeKey() {
  return `${Date.now().toString(36)}${Math.floor(Math.random() * 1e6).toString(36)}`;
}

function ago(ts) {
  const s = Math.max(0, Math.floor(Date.now() / 1000 - ts));
  if (s < 45) return 'just now';
  if (s < 3600) return `${Math.floor(s / 60) || 1}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

const sum = (list, pick) => list.reduce((s, x) => s + (pick(x) || 0), 0);
const signed = (n) => (n > 0 ? '+' : n < 0 ? '−' : '') + Math.abs(n).toLocaleString();
const netColor = (n) => (n > 0 ? '#7ef0a0' : n < 0 ? '#ff8f86' : '#8b8b9c');
// The prize worth the most points, or — when nothing in the case has a points
// value — simply the rarest one.
function topPrize(drops) {
  if (!drops || !drops.length) return null;
  const valued = drops.filter((d) => d.points_value);
  return valued.length ? valued.reduce((m, d) => (d.points_value > m.points_value ? d : m)) : rarest(drops);
}
const SORTS = {
  cheapest: ['Cheapest', (a, b) => a.price - b.price],
  priciest: ['Priciest', (a, b) => b.price - a.price],
  popular: ['Most opened', (a, b) => (b.openCount || 0) - (a.openCount || 0)],
};

function miniDims(qty) {
  if (qty === 2) return { vw: 300, tw: 84, h: 130 };
  if (qty === 3) return { vw: 198, tw: 58, h: 116 };
  return { vw: 148, tw: 44, h: 104 };
}

function ReelStrip({ strip, land, tw, vw, h, fast, bonusMode, tick, onLanded }) {
  const [off, setOff] = useState(0);
  const [glide, setGlide] = useState(false);
  const stripRef = useRef(null);
  const vpRef = useRef(null);
  const rafRef = useRef(0);
  const runRef = useRef(false);
  const lastIdxRef = useRef(-1);
  const timerRef = useRef(null);

  useEffect(() => {
    const wipe = () => {
      const el = stripRef.current;
      if (!el) return;
      [...el.children].forEach((k) => {
        const t = k.firstElementChild;
        if (t) {
          t.style.transform = '';
          t.style.filter = '';
          t.style.boxShadow = '';
          t.style.zIndex = '';
          t.style.transition = '';
        }
      });
    };
    const hi = (idx, on, big) => {
      const el = stripRef.current;
      if (!el) return;
      const k = el.children[idx];
      if (!k) return;
      const t = k.firstElementChild;
      if (!t) return;
      if (!on) {
        t.style.transform = '';
        t.style.filter = '';
        t.style.boxShadow = '';
        t.style.zIndex = '';
        t.style.transition = '';
        return;
      }
      const item = strip[idx];
      const glow = item ? (item.masked ? '#5aa9ff' : oc(item.odds)) : '#fff';
      if (big) {
        t.style.transition = 'transform .4s cubic-bezier(.34,1.56,.64,1),filter .4s ease,box-shadow .4s ease';
        t.style.transform = 'scale(1.22)';
        t.style.filter = 'brightness(1.55)';
        t.style.boxShadow = `0 0 22px ${glow}aa,0 0 0 2px ${glow}`;
        t.style.zIndex = 5;
      } else {
        t.style.transform = 'scale(1.05)';
        t.style.filter = 'brightness(1.4)';
        t.style.boxShadow = '0 0 16px rgba(255,255,255,.2)';
      }
    };

    const dur = bonusMode ? (fast ? 2400 : 4200) : fast ? 3600 : 11500;
    const V = (vpRef.current && vpRef.current.offsetWidth) || vw;
    const near = Math.random() < 0.68,
      dir = Math.random() < 0.5 ? -1 : 1,
      jitter = near ? dir * (tw * (0.2 + Math.random() * 0.1)) : (Math.random() - 0.5) * (tw * 0.22);
    const target = -(land * tw + tw / 2 - V / 2 + jitter);

    let r2 = 0;
    const r1 = requestAnimationFrame(() => {
      r2 = requestAnimationFrame(() => {
        setGlide(dur / 1000);
        setOff(target);
      });
    });

    const track = () => {
      const el = stripRef.current;
      if (!el || !runRef.current) return;
      const m = new DOMMatrixReadOnly(getComputedStyle(el).transform),
        x = m.m41,
        i = Math.floor((V / 2 - x) / tw);
      if (i !== lastIdxRef.current) {
        hi(lastIdxRef.current, false);
        hi(i, true, false);
        lastIdxRef.current = i;
        tick();
      }
      rafRef.current = requestAnimationFrame(track);
    };
    runRef.current = true;
    rafRef.current = requestAnimationFrame(track);

    const finish = () => {
      runRef.current = false;
      cancelAnimationFrame(rafRef.current);
      wipe();
      hi(land, true, true);
      onLanded();
    };
    timerRef.current = setTimeout(finish, dur + 200);

    return () => {
      runRef.current = false;
      clearTimeout(timerRef.current);
      cancelAnimationFrame(r1);
      cancelAnimationFrame(r2);
      cancelAnimationFrame(rafRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div
      ref={vpRef}
      className="relative"
      style={{ width: vw, height: h, overflow: 'hidden', borderRadius: 12, background: 'radial-gradient(ellipse at 50% 50%,#1a1a24,#0a0a10)', border: '1px solid #23232e', boxShadow: 'inset 0 0 26px rgba(0,0,0,.7)', flexShrink: 0 }}
    >
      <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: Math.round(vw * 0.24), zIndex: 4, pointerEvents: 'none', background: 'linear-gradient(90deg,#0a0a10,transparent)' }} />
      <div style={{ position: 'absolute', right: 0, top: 0, bottom: 0, width: Math.round(vw * 0.24), zIndex: 4, pointerEvents: 'none', background: 'linear-gradient(270deg,#0a0a10,transparent)' }} />
      <div
        ref={stripRef}
        style={{
          display: 'flex',
          height: '100%',
          alignItems: 'center',
          transform: `translateX(${off}px)`,
          transition: glide ? `transform ${glide}s cubic-bezier(.16,.84,.24,1)` : 'none',
          willChange: 'transform',
        }}
      >
        {(strip || []).map((d, i) => {
          const masked = !!d.masked;
          const color = masked ? '#5aa9ff' : oc(d.odds);
          return (
          <div key={i} style={{ width: tw, flexShrink: 0, height: h - 26, display: 'flex', padding: '0 3px', boxSizing: 'border-box' }}>
            <div
              style={{
                position: 'relative',
                width: '100%',
                height: '100%',
                borderRadius: 9,
                background: `linear-gradient(180deg,#191922,#101017 62%,${color}26)`,
                border: `1px solid ${color}3d`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                overflow: 'hidden',
              }}
            >
              <div style={{ position: 'absolute', left: 0, right: 0, bottom: 0, height: 2, background: color }} />
              {masked ? (
                <span className="material-symbols-outlined om-ray" style={{ color, fontSize: Math.max(14, Math.round(tw * 0.32)) }}>diamond</span>
              ) : d.image_url ? (
                <img src={mediaUrl(d.image_url)} alt={d.name} style={{ width: '72%', height: '72%', objectFit: 'contain' }} />
              ) : (
                <span className="material-symbols-outlined" style={{ color, fontSize: Math.max(14, Math.round(tw * 0.32)) }}>redeem</span>
              )}
            </div>
          </div>
          );
        })}
      </div>
    </div>
  );
}

function Chip({ label, icon, onClick, on }) {
  return (
    <button
      onClick={onClick}
      style={{
        background: on ? 'rgba(255,84,74,.12)' : '#15151d',
        border: '1px solid ' + (on ? 'rgba(255,84,74,.42)' : '#26263a'),
        color: on ? '#ff8f86' : '#8b8b9c',
        borderRadius: 99,
        padding: '8px 14px',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        gap: 6,
      }}
    >
      <span className="material-symbols-outlined" style={{ fontSize: 16 }}>{icon}</span>
      <span className="font-mono-brand font-bold uppercase" style={{ fontSize: 10.5, letterSpacing: '.14em' }}>
        {label}
      </span>
    </button>
  );
}

function PullCard({ p }) {
  const glow = p.odds != null ? oc(p.odds) : '#5aa9ff';
  return (
    <div className="pull-card" style={{ '--rc': glow }}>
      <div className="pull-card-art">
        {p.image_url ? (
          <img src={mediaUrl(p.image_url)} alt={p.drop_name} style={{ width: '82%', height: '82%', objectFit: 'contain' }} />
        ) : (
          <span className="material-symbols-outlined" style={{ fontSize: 22, color: glow }}>redeem</span>
        )}
      </div>
      <div style={{ minWidth: 0, flex: 1 }}>
        <div className="font-bold truncate" style={{ fontSize: 14, color: '#f0f0f6' }}>{p.drop_name}</div>
        <div className="font-mono-brand truncate" style={{ fontSize: 11, color: '#8b8b9c' }}>from {p.case_name}</div>
        <div className="font-mono-brand truncate" style={{ fontSize: 11, color: '#7d7d8c', marginTop: 1 }}>
          <span style={{ color: '#b4b4c2' }}>{p.username}</span> · {ago(p.created_at)}
        </div>
      </div>
      <div className="flex flex-col items-end gap-1.5" style={{ flexShrink: 0 }}>
        {p.odds != null && <span className="odds-pill" style={{ '--rc': glow }}>{pc(p.odds)}</span>}
        {p.points_value ? (
          <span className="font-mono-brand font-bold" style={{ fontSize: 12, color: '#F6BB42' }}>🪙 {p.points_value.toLocaleString()}</span>
        ) : null}
      </div>
    </div>
  );
}

const FAIR_BOX = { fontSize: 11, color: '#c9c9d6', background: '#0a0a11', border: '1px solid #23232e', borderRadius: 9, padding: '9px 12px', wordBreak: 'break-all' };
const FAIR_LABEL = { fontSize: 8.5, letterSpacing: '.22em', color: '#7d7d8c', marginBottom: 6 };

/** The real "verify" — shows the seed commitment, lets the player rotate (which
 *  reveals the old server seed), and re-checks any past opening in the browser. */
function FairPanel({ user, fair, mine, proofs, seedInput, setSeedInput, busy, error, onRotate, onCheck, onLogin }) {
  if (!user) {
    return (
      <div style={{ fontSize: 12.5, color: '#888899', lineHeight: 1.7 }}>
        Sign in to get your own seeds. Every open is rolled from a secret server seed (shown to you as a hash first) and
        a client seed you control, so you can re-check afterwards that nothing was changed.
        <button
          onClick={() => onLogin()}
          className="font-black uppercase"
          style={{ display: 'block', marginTop: 14, background: 'linear-gradient(135deg,#ff6b5f,#ff3d31)', color: '#12060a', border: 'none', borderRadius: 9, padding: '10px 18px', cursor: 'pointer', fontSize: 11.5, letterSpacing: '.1em' }}
        >
          Sign in with Kick
        </button>
      </div>
    );
  }

  const recent = (mine?.pulls || []).slice(0, 8);
  return (
    <div>
      <p style={{ fontSize: 12, color: '#888899', lineHeight: 1.65, margin: '0 0 14px' }}>
        Each open is rolled from your <b style={{ color: '#c9c9d6' }}>server seed</b> (secret, but we show its hash up front so it
        can't be swapped) and your <b style={{ color: '#c9c9d6' }}>client seed</b>. Rotate to reveal the server seed, then check
        any opening below — the math runs in your browser.
      </p>

      <div style={{ marginBottom: 12 }}>
        <div className="font-mono-brand uppercase" style={FAIR_LABEL}>Server seed (hashed)</div>
        <div className="font-mono-brand" style={FAIR_BOX}>{fair ? fair.current.serverSeedHash : 'Loading…'}</div>
      </div>
      <div className="flex gap-3 flex-wrap" style={{ marginBottom: 12 }}>
        <div style={{ flex: '1 1 180px', minWidth: 0 }}>
          <div className="font-mono-brand uppercase" style={FAIR_LABEL}>Client seed</div>
          <div className="font-mono-brand" style={FAIR_BOX}>{fair ? fair.current.clientSeed : '…'}</div>
        </div>
        <div style={{ flex: '0 0 90px' }}>
          <div className="font-mono-brand uppercase" style={FAIR_LABEL}>Opens so far</div>
          <div className="font-mono-brand" style={FAIR_BOX}>{fair ? fair.current.nonce : '…'}</div>
        </div>
      </div>

      <div className="flex gap-2 flex-wrap" style={{ marginBottom: 6 }}>
        <input
          value={seedInput}
          onChange={(e) => setSeedInput(e.target.value.slice(0, 64))}
          placeholder="New client seed (optional)"
          maxLength={64}
          className="font-mono-brand"
          style={{ flex: '1 1 200px', minWidth: 0, background: '#0a0a11', border: '1px solid #26263a', borderRadius: 9, padding: '10px 12px', color: '#fff', fontSize: 12, outline: 'none' }}
        />
        <button
          onClick={onRotate}
          disabled={busy}
          className="font-black uppercase"
          style={{ background: busy ? '#1c1c26' : '#16161e', border: '1px solid #3a3a52', color: busy ? '#7d7d8c' : '#dcdce6', borderRadius: 9, padding: '10px 16px', cursor: busy ? 'not-allowed' : 'pointer', fontSize: 11, letterSpacing: '.08em' }}
        >
          Rotate &amp; reveal
        </button>
      </div>
      {error && <div style={{ fontSize: 11.5, color: '#ff8f86', marginTop: 6 }}>{error}</div>}

      {fair && fair.previous.length > 0 && (
        <div style={{ marginTop: 18 }}>
          <div className="font-mono-brand uppercase" style={FAIR_LABEL}>Revealed server seeds</div>
          <div className="flex flex-col gap-2">
            {fair.previous.slice(0, 3).map((s) => (
              <div key={s.id}>
                <div className="font-mono-brand" style={{ fontSize: 9.5, color: '#7d7d8c', marginBottom: 3 }}>
                  {s.pulls} open{s.pulls === 1 ? '' : 's'} · client seed “{s.clientSeed}”
                </div>
                <div className="font-mono-brand" style={{ ...FAIR_BOX, fontSize: 10, userSelect: 'all' }}>{s.serverSeed}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div style={{ marginTop: 18 }}>
        <div className="font-mono-brand uppercase" style={FAIR_LABEL}>Your latest openings</div>
        {recent.length === 0 ? (
          <div className="font-mono-brand" style={{ fontSize: 11, color: '#43434f' }}>Nothing opened yet — your first open will show up here.</div>
        ) : (
          <div className="flex flex-col">
            {recent.map((p) => {
              const proof = proofs[p.id];
              return (
                <div key={p.id} style={{ borderBottom: '1px solid #1c1c24', padding: '9px 0' }}>
                  <div className="flex items-center gap-2.5">
                    <span style={{ width: 7, height: 7, borderRadius: '50%', background: oc(p.odds ?? 100), flexShrink: 0 }} />
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div className="font-mono-brand font-bold" style={{ fontSize: 11.5, color: '#dcdce6', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.drop_name}</div>
                      <div className="font-mono-brand" style={{ fontSize: 9.5, color: '#7d7d8c' }}>
                        {p.case_name}{p.nonce != null ? ` · roll #${p.nonce}` : ''}
                      </div>
                    </div>
                    {!p.verifiable ? (
                      <span className="font-mono-brand" style={{ fontSize: 9.5, color: '#43434f' }}>before fairness</span>
                    ) : !p.revealed ? (
                      <span className="font-mono-brand" style={{ fontSize: 9.5, color: '#7d7d8c' }}>rotate to reveal</span>
                    ) : (
                      <button
                        onClick={() => onCheck(p.id)}
                        disabled={proof?.state === 'loading'}
                        className="font-mono-brand font-bold uppercase"
                        style={{ background: '#15151d', border: '1px solid #26263a', color: '#5aa9ff', borderRadius: 8, padding: '5px 11px', cursor: 'pointer', fontSize: 9.5, letterSpacing: '.1em' }}
                      >
                        {proof?.state === 'loading' ? 'Checking…' : proof ? 'Check again' : 'Verify'}
                      </button>
                    )}
                  </div>
                  {proof && proof.state !== 'loading' && (
                    <div className="font-mono-brand" style={{ marginTop: 7, paddingLeft: 17, fontSize: 10.5, lineHeight: 1.6 }}>
                      {proof.state === 'error' && <div style={{ color: '#ff8f86' }}>{proof.message}</div>}
                      {proof.state === 'pending' && <div style={{ color: '#7d7d8c' }}>Rotate your seed first — it's revealed then.</div>}
                      {proof.checks.map((c) => (
                        <div key={c.label} style={{ color: c.ok ? '#7ef0a0' : '#ff8f86' }}>
                          {c.ok ? '✓' : '✗'} {c.label}
                        </div>
                      ))}
                      {proof.state === 'verified' && <div style={{ color: '#7ef0a0', fontWeight: 700 }}>Verified — this open was fair.</div>}
                      {proof.state === 'failed' && <div style={{ color: '#ff8f86', fontWeight: 700 }}>A check failed — screenshot this and tell us.</div>}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      <details style={{ marginTop: 16, fontSize: 11, color: '#7d7d8c', lineHeight: 1.65 }}>
        <summary style={{ cursor: 'pointer', color: '#8b8b9c' }} className="font-mono-brand">How the roll is made</summary>
        <div style={{ marginTop: 8 }}>
          roll = first 6 bytes of HMAC-SHA256(key = server seed, message = “clientSeed:nonce”) ÷ 2<sup>48</sup>, a number from 0 up
          to (not including) 1. The case's items are laid out in the order they were added, each taking a slice of the line
          equal to its chance; whichever slice <i>roll</i> lands in is what you get. Each open in a batch uses the next nonce.
          Demo spins aren't recorded and aren't verifiable.
        </div>
      </details>
    </div>
  );
}

export default function Cases() {
  const { user, login, refresh } = useAuth();
  const bal = user ? user.points : 0;

  const [cases, setCases] = useState([]);
  const [open, setOpen] = useState(null);
  const [qty, setQty] = useState(1);
  const [reel, setReel] = useState([]);
  const [off, setOff] = useState(0);
  const [glide, setGlide] = useState(false);
  const [spin, setSpin] = useState(false);
  const [result, setResult] = useState(null);
  const [results, setResults] = useState(null);
  const [batchStrips, setBatchStrips] = useState([]);
  const [batchNonce, setBatchNonce] = useState(0);
  // The player's own lifetime stats + latest openings (server-side, so they
  // survive a refresh), and their provably-fair seed state.
  const [mine, setMine] = useState(null);
  const [fair, setFair] = useState(null);
  const [proofs, setProofs] = useState({});
  const [seedInput, setSeedInput] = useState('');
  const [fairBusy, setFairBusy] = useState(false);
  const [fairError, setFairError] = useState('');
  const [sort, setSort] = useState('cheapest');
  const [affordableOnly, setAffordableOnly] = useState(false);
  const [recentPulls, setRecentPulls] = useState([]);
  const [topPulls, setTopPulls] = useState([]);
  const [highPulls, setHighPulls] = useState([]);
  const [nonce, setNonce] = useState(0);
  const [fast, setFast] = useState(false);
  const [demo, setDemo] = useState(false);
  const [dialog, setDialog] = useState(null);
  const [bonusSpin, setBonusSpin] = useState(false);
  const [batchBonus, setBatchBonus] = useState(false);
  // Per-slot flag: which batch reels are actively replaying the Diamond
  // Spin bonus wave (rare landings only) vs. sitting static on their
  // already-decided common result.
  const [batchBonusActive, setBatchBonusActive] = useState([]);
  const [rollKey, setRollKey] = useState('');
  const [snd, setSnd] = useState(true);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const sndRef = useRef(true);
  sndRef.current = snd;
  // Lets the cases:pull socket handler (a stale closure from mount) see the
  // live spin state, so it can hold off refreshing the pulls list while a
  // reel is visibly spinning instead of spoiling the reveal early.
  const spinRef = useRef(false);
  spinRef.current = spin;
  const targetRef = useRef(0);
  const baitRef = useRef(null);
  const stripRef = useRef(null);
  const vpRef = useRef(null);
  const viewRef = useRef(VIEW);
  const rafRef = useRef(0);
  const runRef = useRef(false);
  const lastIdxRef = useRef(-1);
  const timerRef = useRef(null);
  const t2Ref = useRef(null);
  const tksRef = useRef([]);
  const acRef = useRef(null);
  const diamondAudioRef = useRef(null);
  const durRef = useRef(11500);
  // Guards state updates from timers that outlive the component (e.g. the
  // Diamond Spin's 2s suspense delay firing after the user navigates away).
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);
  const batchDoneRef = useRef(0);
  const batchWonRef = useRef([]);
  // Mirrors bonusRef but for the batch (qty>1) flow — guards the one-shot
  // bonus replay wave across all reels in the batch.
  const batchBonusRef = useRef(false);
  // How many reels are actually re-spinning during the bonus wave (only the
  // ones that landed rare) — handleReelDone counts against this, not qty.
  const batchBonusTargetRef = useRef(0);
  // Hard one-shot latch: true forever once the bonus wave has played for
  // this open, even after batchBonusRef resets at finalize. Prevents any
  // stray/late onLanded call from re-arming the bonus wave and looping.
  const batchBonusPlayedRef = useRef(false);
  // Set once handleReelDone finalizes a batch open. Any further call (a
  // stray/duplicate onLanded from a reel that should already be gone) is a
  // pure no-op — closes off any path to a repeat/loop.
  const batchFinalizedRef = useRef(true);
  const demoRef = useRef(false);
  // Guards the one-shot cosmetic bonus reel (see finish()) so a rare landing
  // triggers a single replay through only the case's good items, not a loop.
  const bonusRef = useRef(false);
  // Holds the true single-open result while the main reel shows a masked
  // "Special Gem" placeholder instead of spoiling which rare item it is.
  const realWonRef = useRef(null);

  useEffect(() => {
    apiFetch('/api/cases')
      .then((r) => r.json())
      .then(setCases);
  }, []);

  function loadPulls() {
    apiFetch('/api/cases/pulls/recent').then((r) => r.json()).then(setRecentPulls);
    apiFetch('/api/cases/pulls/top').then((r) => r.json()).then(setTopPulls);
    apiFetch('/api/cases/pulls/high').then((r) => r.json()).then(setHighPulls);
  }

  function loadMine() {
    apiFetch('/api/cases/mine')
      .then((r) => (r.ok ? r.json() : null))
      .then((m) => m && setMine(m))
      .catch(() => {});
  }
  function loadFair() {
    apiFetch('/api/cases/fair')
      .then((r) => (r.ok ? r.json() : null))
      .then((f) => f && setFair(f))
      .catch(() => {});
  }
  const username = user ? user.username : null;
  useEffect(() => {
    if (username) loadMine();
    else {
      setMine(null);
      setFair(null);
      setProofs({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [username]);

  function openFairDialog() {
    setDialog('verify');
    setFairError('');
    if (user) {
      loadFair();
      loadMine();
    }
  }

  // Reveals the current server seed (so every pull made with it can be
  // re-checked) and starts a new pair, optionally with the client seed typed in.
  async function rotateSeed() {
    if (fairBusy) return;
    setFairBusy(true);
    setFairError('');
    try {
      const res = await apiFetch('/api/cases/fair/rotate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ clientSeed: seedInput }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Could not rotate the seed');
      setFair(json);
      setSeedInput('');
      setProofs({});
      loadMine();
    } catch (e) {
      setFairError(e.message);
    } finally {
      setFairBusy(false);
    }
  }

  // Fetches the proof for one of the player's openings and re-checks it here
  // in the browser (see lib/fair.js) rather than trusting a server-side "ok".
  async function checkPull(id) {
    setProofs((p) => ({ ...p, [id]: { state: 'loading', checks: [] } }));
    try {
      const res = await apiFetch(`/api/cases/pulls/${id}/proof`);
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Could not load the proof');
      const result = await verifyProof(json);
      setProofs((p) => ({ ...p, [id]: result }));
    } catch (e) {
      setProofs((p) => ({ ...p, [id]: { state: 'error', message: e.message, checks: [] } }));
    }
  }

  useEffect(() => {
    loadPulls();
    const socket = io(API_BASE || undefined, { path: '/socket.io' });
    // While a reel is visibly spinning, hold off — refreshing this list (or
    // the balance) the instant the server responds would spoil the reveal
    // before the animation even lands. Catching up happens once the spin
    // that's in progress finishes, in finish()/handleReelDone() below.
    socket.on('cases:pull', () => {
      if (!spinRef.current) loadPulls();
    });
    return () => socket.disconnect();
  }, []);

  const audio = () => {
    try {
      if (!acRef.current) acRef.current = new (window.AudioContext || window.webkitAudioContext)();
      if (acRef.current.state === 'suspended') acRef.current.resume();
      return acRef.current;
    } catch {
      return null;
    }
  };
  const tick = () => {
    if (!sndRef.current) return;
    const a = audio();
    if (!a) return;
    const t = a.currentTime,
      o = a.createOscillator(),
      g = a.createGain();
    o.type = 'square';
    o.frequency.value = 1150 + Math.random() * 120;
    g.gain.setValueAtTime(0.05, t);
    g.gain.exponentialRampToValueAtTime(0.0006, t + 0.05);
    o.connect(g);
    g.connect(a.destination);
    o.start(t);
    o.stop(t + 0.06);
  };
  const chime = (big) => {
    if (!sndRef.current) return;
    const a = audio();
    if (!a) return;
    const t = a.currentTime,
      notes = big ? [523.25, 659.25, 783.99, 1046.5] : [523.25, 659.25, 783.99];
    notes.forEach((f, i) => {
      const o = a.createOscillator(),
        g = a.createGain(),
        d = i * 0.1;
      o.type = 'triangle';
      o.frequency.value = f;
      g.gain.setValueAtTime(0.0001, t + d);
      g.gain.exponentialRampToValueAtTime(0.16, t + d + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0005, t + d + 0.6);
      o.connect(g);
      g.connect(a.destination);
      o.start(t + d);
      o.stop(t + d + 0.65);
    });
  };
  // Real recording (not a synthesized tone like tick()/chime() above), played
  // once at the exact moment a Diamond Spin is triggered — reused across
  // plays via a single Audio instance rather than a fresh `new Audio()` per
  // pull.
  const playDiamondSpinSound = () => {
    if (!sndRef.current) return;
    try {
      if (!diamondAudioRef.current) diamondAudioRef.current = new Audio('/sounds/diamond-spin.mp3');
      diamondAudioRef.current.currentTime = 0;
      diamondAudioRef.current.play().catch(() => {});
    } catch {
      // ignore — playback is a nice-to-have, never blocks the spin
    }
  };
  const wipe = () => {
    const el = stripRef.current;
    if (!el) return;
    [...el.children].forEach((k) => {
      const t = k.firstElementChild;
      if (t) {
        t.style.transform = '';
        t.style.filter = '';
        t.style.boxShadow = '';
        t.style.zIndex = '';
        t.style.transition = '';
      }
    });
  };
  const clearAll = () => {
    runRef.current = false;
    cancelAnimationFrame(rafRef.current);
    wipe();
    clearTimeout(timerRef.current);
    clearTimeout(t2Ref.current);
    tksRef.current.forEach(clearTimeout);
    tksRef.current = [];
  };
  useEffect(() => clearAll, []);

  const openCase = cases.find((cs) => cs.id === open);
  // The most-opened case gets a "Popular" badge — only meaningful when there's
  // a choice and someone has actually opened one.
  const popularId = cases.length > 1 ? cases.reduce((m, c2) => ((c2.openCount || 0) > (m.openCount || 0) ? c2 : m), cases[0]) : null;
  const popularCaseId = popularId && popularId.openCount > 0 ? popularId.id : null;
  const shownCases = cases
    .filter((cs) => !affordableOnly || !user || cs.price <= bal)
    .sort(SORTS[sort][1]);

  // Keyboard: Esc closes the dialog / case, Enter opens, 1-4 picks the
  // quantity. Ignored while typing in a field, mid-spin, or with a dialog up.
  useEffect(() => {
    if (!openCase) return;
    const onKey = (e) => {
      if (e.target.closest && e.target.closest('input,textarea,select')) return;
      if (e.key === 'Escape') {
        if (dialog) setDialog(null);
        else if (!spin) {
          setOpen(null);
          resetModal();
        }
        return;
      }
      if (dialog || spin || busy) return;
      if (e.key >= '1' && e.key <= '4') changeQty(Number(e.key));
      else if (e.key === 'Enter' && !(e.target.closest && e.target.closest('button,a'))) start(openCase, false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  function changeQty(n) {
    if (spin || busy) return;
    setQty(n);
    setResult(null);
    setResults(null);
    setReel([]);
    setOff(0);
    setGlide(false);
    setBatchStrips([]);
    setError('');
  }

  async function start(cs, isDemo) {
    if (spin || busy) return;
    if (!isDemo && !user) {
      login();
      return;
    }
    const n = qty;
    if (!isDemo && cs.price * n > bal) return;
    clearAll();
    audio();
    demoRef.current = !!isDemo;
    setDemo(!!isDemo);
    bonusRef.current = false;
    setBonusSpin(false);
    setResult(null);
    setResults(null);
    setBatchStrips([]);
    setRollKey(makeKey());
    setError('');
    durRef.current = fast ? 3600 : 11500;

    let won;
    if (isDemo) {
      won = Array.from({ length: n }, () => pick(cs.drops));
    } else {
      setBusy(true);
      try {
        const res = await apiFetch(`/api/cases/${cs.id}/open`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ qty: n }),
        });
        const json = await res.json();
        if (!res.ok) throw new Error(json.error || 'Open failed');
        won = json.drops || [json.drop];
        // Balance stays as-is on screen until the reel actually lands (see
        // finish()/handleReelDone()) — updating it now would show the win/loss
        // before the spin even finishes, spoiling it.
      } catch (e) {
        setError(e.message);
        setBusy(false);
        return;
      }
      setBusy(false);
    }

    if (n > 1) {
      const fillerPool = commonPool(cs.drops);
      const strips = won.map((w) => {
        const strip = [];
        for (let i = 0; i < 80; i++) strip.push(pick(fillerPool));
        // Rare landings are masked here too — revealed for real only after
        // the bonus wave plays (see handleReelDone()).
        strip[LAND] = w.odds <= 10 ? { ...w, masked: true } : w;
        return strip;
      });
      batchWonRef.current = won;
      batchDoneRef.current = 0;
      batchBonusRef.current = false;
      batchBonusPlayedRef.current = false;
      batchFinalizedRef.current = false;
      batchBonusTargetRef.current = 0;
      setBatchBonus(false);
      setBatchBonusActive([]);
      setBatchStrips(strips);
      setSpin(true);
      setBatchNonce((k) => k + 1);
      return;
    }

    const singleWon = won[0];
    realWonRef.current = singleWon;
    const strip = [];
    for (let i = 0; i < 80; i++) strip.push(pick(commonPool(cs.drops)));
    // Rare landings are masked on the main reel — the player sees a mystery
    // "Special Gem" here, not which specific item it is. That's revealed
    // only after the bonus reel (see finish()) plays through the good items.
    strip[LAND] = singleWon.odds <= 10 ? { ...singleWon, masked: true } : singleWon;

    const V = (vpRef.current && vpRef.current.offsetWidth) || VIEW;
    viewRef.current = V;
    const near = Math.random() < 0.68,
      dir = Math.random() < 0.5 ? -1 : 1,
      jitter = near ? dir * (CW * (0.2 + Math.random() * 0.1)) : (Math.random() - 0.5) * (CW * 0.22);
    targetRef.current = -(LAND * CW + CW / 2 - V / 2 + jitter);
    // Diamond Spin bait: the reel visually slows to a stop one tile short of
    // the masked Special Gem (a plain common item), holds there, then creeps
    // that last tile forward to actually land — see the `bait`/`creep`
    // handling below. Purely a suspense beat: the outcome was already fixed
    // by the server, this doesn't touch it.
    if (singleWon.odds <= 10) {
      const baitDir = Math.random() < 0.5 ? -1 : 1;
      const baitJitter = (Math.random() - 0.5) * (CW * 0.22);
      baitRef.current = -((LAND + baitDir) * CW + CW / 2 - V / 2 + baitJitter);
    } else {
      baitRef.current = null;
    }
    setGlide(false);
    setOff(0);
    setReel(strip);
    setSpin(true);
    setNonce((n) => n + 1);
  }

  function handleReelDone() {
    if (batchFinalizedRef.current) return;
    batchDoneRef.current += 1;
    const target = batchBonusRef.current ? batchBonusTargetRef.current : batchWonRef.current.length;
    if (batchDoneRef.current >= target) {
      // Same idea as the single-open bonus reel: if any reel in the batch
      // landed rare, replay just those reels through the case's good items
      // before revealing anything — common slots stay put, already settled.
      if (!batchBonusRef.current && !batchBonusPlayedRef.current) {
        const rareIdx = batchWonRef.current
          .map((w, i) => (w.odds <= 10 ? i : -1))
          .filter((i) => i >= 0);
        if (rareIdx.length && openCase) {
          batchBonusRef.current = true;
          batchBonusPlayedRef.current = true;
          setBatchBonus(true);
          playDiamondSpinSound();
          batchBonusTargetRef.current = rareIdx.length;
          const active = batchWonRef.current.map((w) => w.odds <= 10);
          setBatchBonusActive(active);
          // Hold on the mystery gems for a beat before the Diamond Spin kicks in.
          setTimeout(() => {
            if (!mountedRef.current || !openCase) return;
            const rareDrops = openCase.drops.filter((d) => d.odds <= 10);
            const bonusStrips = batchWonRef.current.map((w, i) => {
              if (!active[i]) return null;
              const strip = [];
              for (let k = 0; k < 80; k++) strip.push(pick(rareDrops.length ? rareDrops : openCase.drops));
              strip[LAND] = w;
              return strip;
            });
            batchDoneRef.current = 0;
            setBatchStrips(bonusStrips);
            setBatchNonce((k) => k + 1);
          }, 2000);
          return;
        }
      }

      batchFinalizedRef.current = true;
      setSpin(false);
      batchBonusRef.current = false;
      setBatchBonus(false);
      setBatchBonusActive([]);
      setResults(batchWonRef.current);
      chime(batchWonRef.current.some((w) => w.odds <= 10));
      if (!demoRef.current) {
        refresh();
        loadPulls();
        loadMine();
        loadFair();
      }
    }
  }

  useEffect(() => {
    if (!spin || !reel.length) return;
    let r2 = 0;
    const dur = durRef.current,
      bait = baitRef.current,
      creep = fast ? 700 : 1250;

    const r1 = requestAnimationFrame(() => {
      r2 = requestAnimationFrame(() => {
        setGlide(dur / 1000);
        setOff(bait !== null ? bait : targetRef.current);
      });
    });

    lastIdxRef.current = -1;
    wipe();
    let lastX = null,
      still = 0,
      moved = false,
      t0 = 0;
    const hi = (idx, on, big) => {
      const el = stripRef.current;
      if (!el) return;
      const k = el.children[idx];
      if (!k) return;
      const t = k.firstElementChild;
      if (!t) return;
      if (!on) {
        t.style.transform = '';
        t.style.filter = '';
        t.style.boxShadow = '';
        t.style.zIndex = '';
        t.style.transition = '';
        return;
      }
      const item = reel[idx];
      const glow = item ? oc(item.odds) : '#fff';
      if (big) {
        t.style.transition = 'transform .4s cubic-bezier(.34,1.56,.64,1),filter .4s ease,box-shadow .4s ease';
        t.style.transform = 'scale(1.3)';
        t.style.filter = 'brightness(1.55)';
        t.style.boxShadow = `0 0 34px ${glow}aa,0 0 0 3px ${glow}`;
        t.style.zIndex = 5;
      } else {
        t.style.transform = 'scale(1.05)';
        t.style.filter = 'brightness(1.45)';
        t.style.boxShadow = '0 0 24px rgba(255,255,255,.22)';
      }
    };
    const track = (ts) => {
      const el = stripRef.current;
      if (!el || !runRef.current) return;
      const m = new DOMMatrixReadOnly(getComputedStyle(el).transform),
        x = m.m41,
        i = Math.floor((viewRef.current / 2 - x) / CW);
      if (i !== lastIdxRef.current) {
        hi(lastIdxRef.current, false);
        hi(i, true, false);
        lastIdxRef.current = i;
        tick();
      }
      if (!t0) t0 = ts || performance.now();
      if (lastX !== null && Math.abs(x - lastX) > 0.05) moved = true;
      if (moved && lastX !== null && Math.abs(x - lastX) < 0.05 && (ts || performance.now()) - t0 > 600) {
        if (still++ > 25) return;
      } else still = 0;
      lastX = x;
      rafRef.current = requestAnimationFrame(track);
    };
    runRef.current = true;
    rafRef.current = requestAnimationFrame(track);

    const finish = () => {
      runRef.current = false;
      cancelAnimationFrame(rafRef.current);
      wipe();
      hi(LAND, true, true);
      // reel[LAND] may be a masked placeholder (see start()) — the real
      // result always lives in realWonRef, set once per open. Both are
      // guarded defensively since resetModal() can null them out if the
      // modal is torn down mid-sequence.
      const landed = reel[LAND];
      if (!landed) return;
      const won = landed.masked ? realWonRef.current : landed;
      if (!won) return;

      // Rare landing, first time through: replay a short cosmetic reel built
      // only from this case's other good (low-chance) drops before settling
      // on the same already-decided result. Purely a celebration — the win
      // itself was fixed the moment the server responded in start().
      const rareDrops = openCase ? openCase.drops.filter((d) => d.odds <= 10) : [];
      if (won.odds <= 10 && !bonusRef.current && rareDrops.length) {
        bonusRef.current = true;
        setBonusSpin(true);
        playDiamondSpinSound();
        // Hold on the mystery gem for a beat before the Diamond Spin kicks in.
        t2Ref.current = setTimeout(() => {
          if (!mountedRef.current || !openCase) return;
          const V = viewRef.current;
          if (!V) return;
          const bonusStrip = [];
          for (let i = 0; i < 80; i++) bonusStrip.push(pick(rareDrops));
          bonusStrip[LAND] = won;
          const near = Math.random() < 0.68,
            dir = Math.random() < 0.5 ? -1 : 1,
            jitter = near ? dir * (CW * (0.2 + Math.random() * 0.1)) : (Math.random() - 0.5) * (CW * 0.22);
          targetRef.current = -(LAND * CW + CW / 2 - V / 2 + jitter);
          baitRef.current = null;
          durRef.current = fast ? 2400 : 4200;
          setGlide(false);
          setOff(0);
          setReel(bonusStrip);
          setNonce((n) => n + 1);
        }, 2000);
        return;
      }

      setSpin(false);
      bonusRef.current = false;
      setBonusSpin(false);
      setResult(won);
      chime(won.odds <= 10);
      if (!demo) {
        refresh();
        loadPulls();
        loadMine();
        loadFair();
      }
    };
    if (bait !== null) {
      timerRef.current = setTimeout(() => {
        setGlide(creep / 1000);
        setOff(targetRef.current);
        [0.35, 0.7, 0.95].forEach((f) => tksRef.current.push(setTimeout(tick, creep * f)));
      }, dur + 340);
      t2Ref.current = setTimeout(finish, dur + 340 + creep + 160);
    } else {
      timerRef.current = setTimeout(finish, dur + 200);
    }
    return () => {
      runRef.current = false;
      cancelAnimationFrame(r1);
      cancelAnimationFrame(r2);
      cancelAnimationFrame(rafRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nonce]);

  function resetModal() {
    clearAll();
    setQty(1);
    setResult(null);
    setResults(null);
    setBatchStrips([]);
    setReel([]);
    setOff(0);
    setGlide(false);
    setSpin(false);
    setDemo(false);
    setDialog(null);
    setError('');
    bonusRef.current = false;
    realWonRef.current = null;
    setBonusSpin(false);
    batchBonusRef.current = false;
    batchBonusPlayedRef.current = false;
    batchFinalizedRef.current = true;
    batchBonusTargetRef.current = 0;
    setBatchBonus(false);
    setBatchBonusActive([]);
  }

  const status = spin
    ? bonusSpin || batchBonus
      ? ['Diamond Spin', '#5aa9ff']
      : ['Opening', '#F6BB42']
    : result || (results && results.length)
      ? [demo ? 'Demo result' : 'Won', '#7ef0a0']
      : ['Ready', '#7a7a8c'];

  return (
    <div className="max-w-[1200px] mx-auto px-6 py-12 relative z-10">
      <header className="relative mb-10">
        <div className="cases-hero-glow" aria-hidden="true" />
        <div className="relative flex items-end justify-between gap-6 flex-wrap">
          <div>
            <div className="cases-eyebrow">
              <span className="live-dot" /> Open &amp; Win
            </div>
            <h1 className="cases-title font-display-lg text-4xl sm:text-5xl font-black tracking-tight">Case Unboxing</h1>
            <p className="text-[#9a9aab] text-[15px] mt-3 max-w-xl leading-relaxed">
              Spend your points on cases, pull rare drops, and re-check every roll yourself.
            </p>
            <div className="flex flex-wrap gap-2 mt-4">
              {[
                ['verified', 'Provably fair'],
                ['bolt', 'Instant point payouts'],
                ['redeem', 'Prizes within 48h'],
              ].map(([icon, label]) => (
                <span key={label} className="trust-chip">
                  <span className="material-symbols-outlined">{icon}</span>
                  {label}
                </span>
              ))}
            </div>
          </div>
          <div className="balance-card">
            <span className="balance-coin" aria-hidden="true">🪙</span>
            <div>
              <div className="font-mono-brand uppercase" style={{ fontSize: 10.5, letterSpacing: '.2em', color: '#8b8b9c' }}>
                {user ? 'Your Balance' : 'Not signed in'}
              </div>
              {user ? (
                <div className="font-display-lg font-black text-white" style={{ fontSize: 26, marginTop: 2, lineHeight: 1.1 }}>
                  {bal.toLocaleString()}
                </div>
              ) : (
                <button onClick={() => login()} className="balance-login">
                  Sign in with Kick
                </button>
              )}
            </div>
          </div>
        </div>
      </header>

      {!cases.length ? (
        <div className="text-center text-[#888899] py-20">No cases available yet — check back soon.</div>
      ) : (
        <>
          <div className="flex items-center justify-between gap-3 flex-wrap mb-6">
            <div className="case-seg" role="group" aria-label="Sort cases">
              {Object.entries(SORTS).map(([key, [label]]) => (
                <button key={key} aria-pressed={sort === key} onClick={() => setSort(key)}>
                  <span className="material-symbols-outlined">
                    {key === 'popular' ? 'local_fire_department' : key === 'cheapest' ? 'trending_up' : 'trending_down'}
                  </span>
                  {label}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-3">
              {user && (
                <button className="case-toggle" aria-pressed={affordableOnly} onClick={() => setAffordableOnly((a) => !a)}>
                  <span className="material-symbols-outlined">savings</span>
                  Can afford
                </button>
              )}
              <span className="font-mono-brand text-[#7d7d8c]" style={{ fontSize: 11.5 }}>
                {shownCases.length} case{shownCases.length === 1 ? '' : 's'}
              </span>
            </div>
          </div>
          {!shownCases.length && (
            <div className="text-center text-[#888899] py-16 font-mono-brand text-sm">
              Nothing at your balance yet — turn off “Can afford” to browse every case.
            </div>
          )}
          <div className="grid gap-6 mb-14" style={{ gridTemplateColumns: 'repeat(auto-fill,minmax(285px,1fr))' }}>
            {shownCases.map((cs, i) => {
              const top = topPrize(cs.drops);
              const others = (cs.drops || []).filter((d) => !top || d.id !== top.id).sort((a, b) => a.odds - b.odds);
              const short = user && cs.price > bal ? cs.price - bal : 0;
              return (
                <div key={cs.id} className="case-card" style={{ '--accent': cs.accent, animationDelay: `${i * 70}ms` }}>
                  <div className="case-art">
                    <div className="case-art-grid" />
                    {cs.id === popularCaseId && (
                      <span className="case-badge" style={{ left: 12, color: '#ffb37a', borderColor: 'rgba(255,140,60,.4)' }}>
                        <span className="material-symbols-outlined">local_fire_department</span>Popular
                      </span>
                    )}
                    {cs.drops && cs.drops.length > 0 && (
                      <span className="case-badge" style={{ right: 12 }}>{cs.drops.length} items</span>
                    )}
                    {cs.image_url ? (
                      <img src={mediaUrl(cs.image_url)} alt={cs.name} className="case-img" />
                    ) : (
                      <span className="material-symbols-outlined case-icon">{cs.icon}</span>
                    )}
                    <div className="case-floor" />
                  </div>

                  <div className="case-body">
                    <h3 className="font-display-lg font-black text-white" style={{ fontSize: 21, lineHeight: 1.15 }}>{cs.name}</h3>

                    {top && (
                      <div className="case-prize" style={{ '--rc': oc(top.odds) }}>
                        <div className="case-prize-art">
                          {top.image_url ? (
                            <img src={mediaUrl(top.image_url)} alt="" />
                          ) : (
                            <span className="material-symbols-outlined" style={{ color: oc(top.odds), fontSize: 22 }}>redeem</span>
                          )}
                        </div>
                        <div style={{ minWidth: 0, flex: 1 }}>
                          <div className="font-mono-brand uppercase" style={{ fontSize: 10, letterSpacing: '.18em', color: '#7d7d8c' }}>Top prize</div>
                          <div className="font-bold truncate" style={{ fontSize: 14, color: oc(top.odds), marginTop: 1 }}>{top.name}</div>
                          <div className="font-mono-brand" style={{ fontSize: 11.5, color: '#9a9aab', marginTop: 1 }}>
                            {top.points_value ? <span style={{ color: '#F6BB42', fontWeight: 700 }}>🪙 {top.points_value.toLocaleString()}</span> : 'Prize item'}
                            <span style={{ color: '#6a6a78' }}> · {pc(top.odds)} chance</span>
                          </div>
                        </div>
                      </div>
                    )}

                    {others.length > 0 && (
                      <div className="flex items-center gap-2.5" title="Also in this case">
                        <div className="flex" style={{ flexShrink: 0 }}>
                          {others.slice(0, 5).map((d, k) => (
                            <div key={d.id} className="case-mini" style={{ marginLeft: k ? -8 : 0, '--rc': oc(d.odds) }}>
                              {d.image_url ? (
                                <img src={mediaUrl(d.image_url)} alt="" />
                              ) : (
                                <span className="material-symbols-outlined" style={{ fontSize: 14, color: oc(d.odds) }}>redeem</span>
                              )}
                            </div>
                          ))}
                        </div>
                        <span className="font-mono-brand" style={{ fontSize: 11, color: '#7d7d8c' }}>
                          {others.length > 5 ? `+${others.length - 5} more drops` : 'also inside'}
                        </span>
                      </div>
                    )}

                    {/* Button + footer share one bottom-anchored group with a
                        fixed footer height, so every card's button lines up
                        whatever its footer says. */}
                    <div className="flex flex-col gap-2.5" style={{ marginTop: 'auto', paddingTop: 4 }}>
                      <button
                        onClick={() => {
                          resetModal();
                          setOpen(cs.id);
                        }}
                        className={`case-open ${short > 0 ? 'is-short' : ''}`}
                      >
                        <span className="flex-1 flex items-center justify-center gap-2" style={{ padding: '14px 0' }}>
                          <span className="material-symbols-outlined" style={{ fontSize: 18 }}>lock_open</span>
                          <span style={{ fontSize: 13, letterSpacing: '.1em' }}>Open Case</span>
                        </span>
                        <span className="case-open-price">
                          <span style={{ fontSize: 14 }}>🪙</span>
                          <span className="font-mono-brand font-black" style={{ fontSize: 13.5 }}>{cs.price.toLocaleString()}</span>
                        </span>
                      </button>
                      <div style={{ minHeight: 30 }}>
                        {short > 0 ? (
                          <>
                            <div style={{ height: 4, borderRadius: 99, background: '#1c1c26', overflow: 'hidden' }}>
                              <div style={{ width: `${Math.min(100, (bal / cs.price) * 100)}%`, height: '100%', background: cs.accent, borderRadius: 99 }} />
                            </div>
                            <div className="font-mono-brand text-center" style={{ fontSize: 11, color: '#8b8b9c', marginTop: 6 }}>
                              🪙 {short.toLocaleString()} more to open
                            </div>
                          </>
                        ) : (
                          <div className="font-mono-brand text-center" style={{ fontSize: 11, color: '#7d7d8c', paddingTop: 6 }}>
                            Opened {(cs.openCount || 0).toLocaleString()} times
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      {user && mine && mine.stats.opened > 0 && (
        <section className="case-panel mb-14">
          <div className="flex items-center gap-3 mb-6">
            <span className="feed-icon material-symbols-outlined">receipt_long</span>
            <div>
              <h2 className="feed-title">Your Case History</h2>
              <p className="feed-sub">Saved to your account — every open, even after a refresh</p>
            </div>
          </div>
          <div className="grid gap-6 lg:grid-cols-[minmax(0,5fr)_minmax(0,7fr)]">
            <div>
              <div className="grid grid-cols-2 gap-3">
                {[
                  ['Opened', mine.stats.opened.toLocaleString(), '#f0f0f6', 'inventory_2'],
                  ['Spent', `🪙 ${mine.stats.spent.toLocaleString()}`, '#f0f0f6', 'shopping_bag'],
                  ['Won', `🪙 ${mine.stats.won.toLocaleString()}`, '#F6BB42', 'emoji_events'],
                  ['Net', signed(mine.stats.net), netColor(mine.stats.net), 'trending_up'],
                ].map(([label, value, color, icon]) => (
                  <div key={label} className="stat-tile">
                    <div className="flex items-center gap-1.5 font-mono-brand uppercase" style={{ fontSize: 10.5, letterSpacing: '.16em', color: '#7d7d8c' }}>
                      <span className="material-symbols-outlined" style={{ fontSize: 15 }}>{icon}</span>
                      {label}
                    </div>
                    <div className="font-display-lg font-black" style={{ fontSize: 22, color, marginTop: 6, lineHeight: 1.1 }}>{value}</div>
                  </div>
                ))}
              </div>
              {(mine.stats.rarest || (mine.stats.biggest && mine.stats.biggest.points_value > 0)) && (
                <div className="flex flex-col gap-2.5 mt-3">
                  {mine.stats.rarest && (
                    <div className="stat-tile flex items-center gap-3" style={{ '--rc': oc(mine.stats.rarest.odds) }}>
                      <span className="material-symbols-outlined" style={{ color: oc(mine.stats.rarest.odds), fontSize: 22 }}>diamond</span>
                      <div style={{ minWidth: 0 }}>
                        <div className="font-mono-brand uppercase" style={{ fontSize: 10, letterSpacing: '.16em', color: '#7d7d8c' }}>Rarest pull</div>
                        <div className="font-bold truncate" style={{ fontSize: 14, color: oc(mine.stats.rarest.odds) }}>
                          {mine.stats.rarest.name} <span style={{ color: '#7d7d8c', fontWeight: 500 }}>· {pc(mine.stats.rarest.odds)}</span>
                        </div>
                      </div>
                    </div>
                  )}
                  {mine.stats.biggest && mine.stats.biggest.points_value > 0 && (
                    <div className="stat-tile flex items-center gap-3">
                      <span className="material-symbols-outlined" style={{ color: '#F6BB42', fontSize: 22 }}>workspace_premium</span>
                      <div style={{ minWidth: 0 }}>
                        <div className="font-mono-brand uppercase" style={{ fontSize: 10, letterSpacing: '.16em', color: '#7d7d8c' }}>Biggest win</div>
                        <div className="font-bold truncate" style={{ fontSize: 14, color: '#F6BB42' }}>
                          {mine.stats.biggest.name} <span style={{ color: '#9a9aab', fontWeight: 500 }}>· 🪙 {mine.stats.biggest.points_value.toLocaleString()}</span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
            <div className="flex flex-col">
              {mine.pulls.slice(0, 8).map((p) => {
                const net = (p.points_value || 0) - (p.price || 0);
                const rc = oc(p.odds ?? 100);
                return (
                  <div key={p.id} className="history-row" style={{ '--rc': rc }}>
                    <div className="pull-card-art" style={{ width: 40, height: 40 }}>
                      {p.image_url ? (
                        <img src={mediaUrl(p.image_url)} alt="" style={{ width: '82%', height: '82%', objectFit: 'contain' }} />
                      ) : (
                        <span className="material-symbols-outlined" style={{ fontSize: 18, color: rc }}>redeem</span>
                      )}
                    </div>
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div className="font-bold truncate" style={{ fontSize: 14, color: '#f0f0f6' }}>{p.drop_name}</div>
                      <div className="font-mono-brand truncate" style={{ fontSize: 11, color: '#7d7d8c' }}>
                        {p.case_name} · {ago(p.created_at)}
                      </div>
                    </div>
                    {p.odds != null && <span className="odds-pill" style={{ '--rc': rc }}>{pc(p.odds)}</span>}
                    {p.price ? (
                      <span className="font-mono-brand font-bold" style={{ fontSize: 12.5, color: netColor(net), minWidth: 56, textAlign: 'right' }}>
                        {p.points_value ? signed(net) : 'prize'}
                      </span>
                    ) : null}
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      <div className="flex flex-col gap-12">
        {[
          ['Recent Openings', 'history', recentPulls, 'What the community just opened', true],
          ['Top Openings', 'emoji_events', topPulls, 'The biggest point payouts so far'],
          ['High Openings', 'diamond', highPulls, 'The rarest pulls ever'],
        ].map(([label, icon, pulls, sub, live]) => (
          <section key={label}>
            <div className="flex items-center gap-3 mb-4">
              <span className="feed-icon material-symbols-outlined">{icon}</span>
              <div>
                <h2 className="feed-title">
                  {label}
                  {live && (
                    <span className="feed-live">
                      <span className="live-dot" /> Live
                    </span>
                  )}
                </h2>
                <p className="feed-sub">{sub}</p>
              </div>
            </div>
            {pulls.length ? (
              <div className="feed-scroll flex gap-3 overflow-x-auto pb-3">
                {pulls.map((p) => (
                  <PullCard key={p.id} p={p} />
                ))}
              </div>
            ) : (
              <div
                className="flex items-center justify-center"
                style={{ minHeight: 96, borderRadius: 16, border: '1px dashed #2a2a36', background: '#0d0d1380' }}
              >
                <span className="font-mono-brand" style={{ fontSize: 12, color: '#5a5a66' }}>Nothing here yet</span>
              </div>
            )}
          </section>
        ))}
      </div>

      {openCase && createPortal(
        <div
          className="fixed inset-0 z-[70] flex items-center justify-center p-5"
          onClick={() => {
            if (!spin) {
              setOpen(null);
              resetModal();
            }
          }}
        >
          <div className="absolute inset-0" style={{ background: 'rgba(0,0,0,.9)' }} />
          <div
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-label={`Open ${openCase.name}`}
            style={{
              background: '#0c0c12', border: `1px solid ${openCase.accent}38`, borderRadius: 22, maxWidth: 760, maxHeight: '92vh', overflowY: 'auto',
              boxShadow: `0 0 90px -28px ${openCase.accent}66`,
            }}
            className="relative w-full dark-scroll"
          >
            <div style={{ position: 'absolute', left: 0, right: 0, top: 0, height: 3, zIndex: 1, background: `linear-gradient(90deg,transparent,${openCase.accent},transparent)` }} />
            <div className="flex items-center justify-between gap-4 flex-wrap" style={{ padding: '20px 24px', borderBottom: '1px solid #1c1c26' }}>
              <button
                onClick={() => {
                  setOpen(null);
                  resetModal();
                }}
                disabled={spin}
                style={{ background: '#15151d', border: '1px solid #26263a', color: spin ? '#43434f' : '#8b8b9c', borderRadius: 9, padding: '8px 14px', cursor: spin ? 'not-allowed' : 'pointer' }}
                className="flex items-center gap-1.5"
              >
                <span className="material-symbols-outlined" style={{ fontSize: 15 }}>arrow_back</span>
                <span className="font-mono-brand font-bold uppercase" style={{ fontSize: 9.5, letterSpacing: '.16em' }}>Back</span>
              </button>
              <div className="flex items-center gap-2 flex-wrap">
                <Chip label="How to claim" icon="help" onClick={() => setDialog('howto')} on={false} />
                <Chip label="Provably fair" icon="verified" onClick={openFairDialog} on={false} />
                <Chip label="Fast mode" icon="bolt" onClick={() => setFast((f) => !f)} on={fast} />
                <Chip label={snd ? 'Sound on' : 'Sound off'} icon={snd ? 'volume_up' : 'volume_off'} onClick={() => setSnd((s) => !s)} on={snd} />
              </div>
            </div>

            <div style={{ padding: 24 }}>
              <div className="flex flex-col items-center text-center" style={{ marginBottom: 20 }}>
                {openCase.image_url ? (
                  <img
                    src={mediaUrl(openCase.image_url)}
                    alt={openCase.name}
                    style={{ width: 200, height: 150, objectFit: 'contain', marginBottom: 12, filter: `drop-shadow(0 16px 30px ${openCase.accent}55)` }}
                  />
                ) : (
                  <span
                    className="material-symbols-outlined"
                    style={{ color: openCase.accent, fontSize: 74, marginBottom: 12, filter: `drop-shadow(0 12px 26px ${openCase.accent}77)` }}
                  >
                    {openCase.icon}
                  </span>
                )}
                <div className="font-display-lg font-black text-white" style={{ fontSize: 22 }}>{openCase.name}</div>
                <div className="font-mono-brand" style={{ fontSize: 12.5, color: '#9a9aab', marginTop: 5 }}>
                  {demo ? 'Demo spin · nothing deducted' : `${openCase.price.toLocaleString()} points per open`}
                </div>
                <div className="font-mono-brand" style={{ fontSize: 11.5, color: '#6a6a78', marginTop: 3 }}>
                  Opened {(openCase.openCount || 0).toLocaleString()} times
                </div>
                <div className="flex items-center gap-2" style={{ marginTop: 12, background: status[1] + '12', border: '1px solid ' + status[1] + '3d', borderRadius: 99, padding: '7px 14px' }}>
                  <span style={{ width: 6, height: 6, borderRadius: '50%', background: status[1], boxShadow: `0 0 8px ${status[1]}`, animation: spin ? 'kickPulse 1.1s ease-in-out infinite' : 'none' }} />
                  <span className="font-mono-brand font-bold uppercase" style={{ fontSize: 10.5, letterSpacing: '.18em', color: status[1] }}>{status[0]}</span>
                </div>
              </div>

              {qty === 1 && (
                <div
                  ref={vpRef}
                  className="relative"
                  style={{ height: 172, overflow: 'hidden', borderRadius: 16, background: 'radial-gradient(ellipse at 50% 50%,#1a1a24,#0a0a10)', border: '1px solid #23232e', marginBottom: 18, boxShadow: 'inset 0 0 42px rgba(0,0,0,.75)' }}
                >
                  <div style={{ position: 'absolute', inset: 0, opacity: 0.18, backgroundImage: 'radial-gradient(#2e2e3c 1px,transparent 1px)', backgroundSize: '14px 14px', pointerEvents: 'none' }} />
                  <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: 88, zIndex: 4, pointerEvents: 'none', background: 'linear-gradient(90deg,#0a0a10,transparent)' }} />
                  <div style={{ position: 'absolute', right: 0, top: 0, bottom: 0, width: 88, zIndex: 4, pointerEvents: 'none', background: 'linear-gradient(270deg,#0a0a10,transparent)' }} />
                  <div style={{ position: 'absolute', left: '50%', top: 0, transform: 'translateX(-50%)', zIndex: 5, pointerEvents: 'none', width: 0, height: 0, borderLeft: '7px solid transparent', borderRight: '7px solid transparent', borderTop: '8px solid #ff544a', filter: 'drop-shadow(0 0 6px rgba(255,84,74,.7))' }} />
                  <div style={{ position: 'absolute', left: '50%', bottom: 0, transform: 'translateX(-50%)', zIndex: 5, pointerEvents: 'none', width: 0, height: 0, borderLeft: '7px solid transparent', borderRight: '7px solid transparent', borderBottom: '8px solid #ff544a', filter: 'drop-shadow(0 0 6px rgba(255,84,74,.7))' }} />
                  <div style={{ position: 'absolute', left: '50%', top: 0, bottom: 0, transform: 'translateX(-50%)', zIndex: 3, pointerEvents: 'none', width: 1, background: 'rgba(255,84,74,.14)' }} />
                  {bonusSpin && spin && (
                    <div
                      className="om-pull flex items-center gap-1.5"
                      style={{
                        position: 'absolute', top: 10, left: '50%', transform: 'translateX(-50%)', zIndex: 6,
                        background: '#5aa9ff1a', border: '1px solid #5aa9ff55', borderRadius: 99,
                        padding: '5px 12px', pointerEvents: 'none',
                      }}
                    >
                      <span className="material-symbols-outlined om-ray" style={{ fontSize: 12, color: '#5aa9ff' }}>diamond</span>
                      <span className="font-mono-brand font-bold uppercase" style={{ fontSize: 8.5, letterSpacing: '.14em', color: '#5aa9ff' }}>
                        Diamond Spin · good items only
                      </span>
                    </div>
                  )}
                  {reel.length === 0 ? (
                    <div className="h-full flex flex-col items-center justify-center gap-2">
                      <span className="material-symbols-outlined" style={{ color: '#2e2e3c', fontSize: 36 }}>casino</span>
                      <div className="font-mono-brand uppercase" style={{ fontSize: 9.5, letterSpacing: '.22em', color: '#43434f' }}>
                        Press open to unbox
                      </div>
                    </div>
                  ) : (
                    <div
                      ref={stripRef}
                      style={{
                        display: 'flex',
                        height: '100%',
                        alignItems: 'center',
                        transform: `translateX(${off}px)`,
                        transition: glide ? `transform ${glide}s ${glide > 2 ? 'cubic-bezier(.08,.66,.12,1)' : 'cubic-bezier(.16,.84,.24,1)'}` : 'none',
                        willChange: 'transform',
                      }}
                    >
                      {reel.map((d, i) => {
                        const masked = !!d.masked;
                        const color = masked ? '#5aa9ff' : oc(d.odds);
                        return (
                        <div key={i} style={{ width: CW, flexShrink: 0, height: 142, display: 'flex', padding: '0 5px', boxSizing: 'border-box' }}>
                          <div
                            style={{
                              position: 'relative',
                              width: '100%',
                              height: '100%',
                              borderRadius: 12,
                              background: `linear-gradient(180deg,#191922,#101017 62%,${color}26)`,
                              border: `1px solid ${color}3d`,
                              willChange: 'filter,transform',
                              transition: 'transform .13s ease,box-shadow .13s ease,filter .13s ease',
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: 8,
                              overflow: 'hidden',
                              boxShadow: masked || (i === LAND && !spin && result && d.odds <= 10) ? `inset 0 0 26px ${color}44` : 'none',
                            }}
                          >
                            <div style={{ position: 'absolute', left: 0, right: 0, bottom: 0, height: 3, background: color }} />
                            {(masked || (i === LAND && !spin && result && d.odds <= 10)) && (
                              <span
                                className="material-symbols-outlined om-ray"
                                style={{ position: 'absolute', fontSize: 84, color, opacity: 0.28, pointerEvents: 'none' }}
                              >
                                diamond
                              </span>
                            )}
                            <div style={{ position: 'relative', width: 52, height: 52, borderRadius: 11, background: masked || !d.image_url ? `radial-gradient(circle,${color}33,transparent 72%)` : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                              {masked ? (
                                <span className="material-symbols-outlined" style={{ color, fontSize: 26, filter: `drop-shadow(0 3px 10px ${color}88)` }}>diamond</span>
                              ) : d.image_url ? (
                                <img src={mediaUrl(d.image_url)} alt={d.name} style={{ width: '100%', height: '100%', objectFit: 'contain', filter: `drop-shadow(0 3px 10px ${color}66)` }} />
                              ) : (
                                <span className="material-symbols-outlined" style={{ color, fontSize: 24, filter: `drop-shadow(0 3px 10px ${color}88)` }}>redeem</span>
                              )}
                            </div>
                            <div className="font-mono-brand font-bold text-center" style={{ fontSize: 9.5, color: masked ? color : '#dcdce6', padding: '0 7px', lineHeight: 1.35 }}>
                              {masked ? 'Special Gem' : d.name}
                            </div>
                          </div>
                        </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

              {qty === 1 && !spin && result && (
                <div
                  key={rollKey}
                  className="om-pull"
                  style={{
                    position: 'relative', display: 'flex', alignItems: 'center', gap: 16, marginBottom: 18,
                    background: `linear-gradient(135deg,${oc(result.odds)}16,#101016)`,
                    border: `1px solid ${oc(result.odds)}55`, borderRadius: 16, padding: '16px 20px', overflow: 'hidden',
                    boxShadow: result.odds <= 10 ? `0 0 34px ${oc(result.odds)}33` : 'none',
                  }}
                >
                  {result.odds <= 10 && (
                    <>
                      <div
                        className="om-burst"
                        style={{
                          position: 'absolute', left: 46, top: '50%', width: 92, height: 92, marginTop: -46,
                          borderRadius: '50%', border: `2px solid ${oc(result.odds)}`, pointerEvents: 'none',
                        }}
                      />
                      <div
                        className="om-burst"
                        style={{
                          position: 'absolute', left: 46, top: '50%', width: 92, height: 92, marginTop: -46,
                          borderRadius: '50%', border: `2px solid ${oc(result.odds)}`, pointerEvents: 'none',
                          animationDelay: '.22s',
                        }}
                      />
                    </>
                  )}
                  <div
                    style={{
                      position: 'relative', width: 64, height: 64, borderRadius: 14, flexShrink: 0,
                      background: `radial-gradient(circle,${oc(result.odds)}33,transparent 72%)`,
                      border: `1px solid ${oc(result.odds)}66`, display: 'flex', alignItems: 'center', justifyContent: 'center',
                      boxShadow: result.odds <= 10 ? `0 0 26px ${oc(result.odds)}77` : 'none',
                      overflow: 'hidden',
                    }}
                  >
                    {result.odds <= 10 && (
                      <span
                        className="material-symbols-outlined om-ray"
                        style={{ position: 'absolute', fontSize: 70, color: oc(result.odds), opacity: 0.32, pointerEvents: 'none' }}
                      >
                        diamond
                      </span>
                    )}
                    {result.image_url ? (
                      <img src={mediaUrl(result.image_url)} alt={result.name} style={{ position: 'relative', width: '80%', height: '80%', objectFit: 'contain' }} />
                    ) : (
                      <span className="material-symbols-outlined" style={{ position: 'relative', color: oc(result.odds), fontSize: 30 }}>redeem</span>
                    )}
                  </div>
                  <div style={{ position: 'relative', flex: 1, minWidth: 0 }}>
                    <div className="font-mono-brand uppercase font-bold flex items-center gap-1.5" style={{ fontSize: 8.5, letterSpacing: '.2em', color: oc(result.odds), marginBottom: 4 }}>
                      {result.odds <= 10 && (
                        <span className="material-symbols-outlined" style={{ fontSize: 12 }}>diamond</span>
                      )}
                      {result.odds <= 10 ? (demo ? 'Diamond Spin · demo' : 'Diamond Spin') : demo ? 'Demo pull' : 'You won'}
                    </div>
                    <div className="font-display-lg font-black text-white" style={{ fontSize: 17, lineHeight: 1.2 }}>{result.name}</div>
                    <div className="font-mono-brand" style={{ fontSize: 10.5, color: '#8b8b9c', marginTop: 4 }}>
                      {pc(result.odds)} chance
                      {result.points_value ? <span style={{ color: '#F6BB42' }}> · 🪙 {result.points_value.toLocaleString()}</span> : ''}
                    </div>
                    {!demo && (
                      <div className="font-mono-brand" style={{ fontSize: 10.5, color: '#8b8b9c', marginTop: 5 }}>
                        Cost 🪙{openCase.price.toLocaleString()}
                        {result.points_value ? (
                          <>
                            {' '}· Won 🪙{result.points_value.toLocaleString()} ·{' '}
                            <span style={{ color: netColor(result.points_value - openCase.price), fontWeight: 700 }}>
                              {signed(result.points_value - openCase.price)}
                            </span>
                          </>
                        ) : (
                          <> · prize item, no points</>
                        )}
                      </div>
                    )}
                    {!demo && result.nonce != null && (
                      <button
                        onClick={openFairDialog}
                        className="font-mono-brand"
                        style={{ background: 'none', border: 'none', padding: 0, marginTop: 6, fontSize: 9.5, color: '#5aa9ff', cursor: 'pointer', textDecoration: 'underline', textUnderlineOffset: 3 }}
                      >
                        Roll #{result.nonce} · check it was fair
                      </button>
                    )}
                  </div>
                  <span
                    className="om-shine"
                    style={{ position: 'absolute', top: 0, bottom: 0, width: '40%', background: 'linear-gradient(100deg,transparent,rgba(255,255,255,.14),transparent)', pointerEvents: 'none' }}
                  />
                  {result.odds <= 10 && (
                    <span
                      className="om-shine"
                      style={{
                        position: 'absolute', top: 0, bottom: 0, width: '40%',
                        background: `linear-gradient(100deg,transparent,${oc(result.odds)}55,transparent)`,
                        pointerEvents: 'none', animationDelay: '.55s',
                      }}
                    />
                  )}
                </div>
              )}

              {qty > 1 && (
                <div
                  className="relative"
                  style={{
                    minHeight: 172,
                    borderRadius: 16,
                    background: 'radial-gradient(ellipse at 50% 50%,#1a1a24,#0a0a10)',
                    border: '1px solid #23232e',
                    marginBottom: 18,
                    boxShadow: 'inset 0 0 42px rgba(0,0,0,.75)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: 18,
                    overflow: 'hidden',
                  }}
                >
                  <div style={{ position: 'absolute', inset: 0, opacity: 0.18, backgroundImage: 'radial-gradient(#2e2e3c 1px,transparent 1px)', backgroundSize: '14px 14px', pointerEvents: 'none' }} />
                  {!batchStrips.length ? (
                    <div className="flex flex-col items-center gap-2">
                      <span className="material-symbols-outlined" style={{ color: '#2e2e3c', fontSize: 36 }}>casino</span>
                      <div className="font-mono-brand uppercase" style={{ fontSize: 9.5, letterSpacing: '.22em', color: '#43434f' }}>
                        Press open to unbox ×{qty}
                      </div>
                    </div>
                  ) : (
                    <div className="flex flex-wrap items-center justify-center" style={{ gap: 12 }}>
                      {batchStrips.map((strip, i) => {
                        const dims = miniDims(qty);
                        const won = results && results[i];
                        const isStatic = batchBonus && !batchBonusActive[i];
                        const item = batchWonRef.current[i];
                        return (
                          <div key={`${batchNonce}-${i}`} className="flex flex-col items-center" style={{ gap: 8 }}>
                            {isStatic ? (
                              <div
                                style={{
                                  width: dims.vw, height: dims.h, borderRadius: 12,
                                  background: 'radial-gradient(ellipse at 50% 50%,#1a1a24,#0a0a10)',
                                  border: '1px solid #23232e', boxShadow: 'inset 0 0 26px rgba(0,0,0,.7)',
                                  display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                                }}
                              >
                                <div
                                  style={{
                                    width: '78%', height: '72%', borderRadius: 9, position: 'relative', overflow: 'hidden',
                                    background: `linear-gradient(180deg,#191922,#101017 62%,${oc(item.odds)}26)`,
                                    border: `1px solid ${oc(item.odds)}3d`,
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                  }}
                                >
                                  <div style={{ position: 'absolute', left: 0, right: 0, bottom: 0, height: 2, background: oc(item.odds) }} />
                                  {item.image_url ? (
                                    <img src={mediaUrl(item.image_url)} alt={item.name} style={{ width: '72%', height: '72%', objectFit: 'contain' }} />
                                  ) : (
                                    <span className="material-symbols-outlined" style={{ color: oc(item.odds), fontSize: Math.max(14, Math.round(dims.tw * 0.32)) }}>redeem</span>
                                  )}
                                </div>
                              </div>
                            ) : (
                              <ReelStrip
                                strip={strip}
                                land={LAND}
                                tw={dims.tw}
                                vw={dims.vw}
                                h={dims.h}
                                fast={fast}
                                bonusMode={batchBonus}
                                tick={tick}
                                onLanded={handleReelDone}
                              />
                            )}
                            {won && (
                              <div
                                className="om-pull text-center"
                                style={{
                                  position: 'relative', background: `${oc(won.odds)}14`, border: `1px solid ${oc(won.odds)}55`, borderRadius: 12,
                                  padding: '7px 10px', boxShadow: won.odds <= 10 ? `0 0 18px ${oc(won.odds)}55` : 'none', overflow: 'hidden',
                                }}
                              >
                                {won.odds <= 10 && (
                                  <span
                                    className="material-symbols-outlined om-ray"
                                    style={{ position: 'absolute', left: '50%', top: '50%', marginLeft: -20, marginTop: -20, fontSize: 40, color: oc(won.odds), opacity: 0.28, pointerEvents: 'none' }}
                                  >
                                    diamond
                                  </span>
                                )}
                                <div className="font-mono-brand font-bold" style={{ position: 'relative', fontSize: 9, color: '#dcdce6' }}>
                                  {won.name}
                                  {won.points_value ? <span style={{ color: '#F6BB42' }}> · 🪙{won.points_value}</span> : ''}
                                </div>
                                <div className="font-mono-brand font-black" style={{ position: 'relative', fontSize: 9, color: oc(won.odds) }}>{pc(won.odds)}</div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

              {qty > 1 && !spin && !demo && results && results.length > 0 && (() => {
                const spent = openCase.price * results.length;
                const won = sum(results, (r) => r.points_value);
                const prizes = results.filter((r) => !r.points_value).length;
                return (
                  <div
                    className="om-pull font-mono-brand flex items-center justify-center gap-x-5 gap-y-1 flex-wrap text-center"
                    style={{ marginBottom: 18, background: '#101016', border: '1px solid #24242e', borderRadius: 12, padding: '10px 16px', fontSize: 11, color: '#8b8b9c' }}
                  >
                    <span>Spent 🪙{spent.toLocaleString()}</span>
                    <span>Won 🪙{won.toLocaleString()}{prizes ? ` + ${prizes} prize${prizes > 1 ? 's' : ''}` : ''}</span>
                    <span style={{ color: netColor(won - spent), fontWeight: 700 }}>Net {signed(won - spent)}</span>
                    <button
                      onClick={openFairDialog}
                      style={{ background: 'none', border: 'none', padding: 0, fontSize: 9.5, color: '#5aa9ff', cursor: 'pointer', textDecoration: 'underline', textUnderlineOffset: 3 }}
                    >
                      Rolls #{results[0].nonce}–{results[results.length - 1].nonce} · check fairness
                    </button>
                  </div>
                );
              })()}

              {error && <p className="text-sm text-[#ff544a] mb-4 text-center">{error}</p>}

              <div className="flex items-center justify-between gap-4 flex-wrap" style={{ marginBottom: 18 }}>
              <div className="flex items-center gap-3 flex-wrap">
                {(() => {
                  const lock = spin || busy || (!!user && openCase.price * qty > bal);
                  const label = !user
                    ? 'Sign in to open'
                    : spin || busy
                      ? 'Opening'
                      : openCase.price * qty > bal
                        ? 'Not enough points'
                        : result || results
                          ? 'Open again'
                          : 'Open';
                  return (
                    <button
                      onClick={() => start(openCase, false)}
                      disabled={lock}
                      style={{
                        background: lock ? '#1c1c26' : 'linear-gradient(135deg,#ff6b5f,#ff3d31)',
                        color: lock ? '#7d7d8c' : '#12060a',
                        boxShadow: lock ? 'none' : '0 5px 18px rgba(255,84,74,.28)',
                        borderRadius: 11,
                        border: 'none',
                        padding: 0,
                        cursor: lock ? 'not-allowed' : 'pointer',
                        overflow: 'hidden',
                      }}
                      className="font-black uppercase flex items-stretch transition-transform hover:scale-[1.02] active:scale-[0.98]"
                    >
                      <span style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '14px 22px' }}>
                        <span className="material-symbols-outlined" style={{ fontSize: 18 }}>lock_open</span>
                        <span style={{ fontSize: 13, letterSpacing: '.1em' }}>{label}</span>
                      </span>
                      {!lock && (
                        <span style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '14px 18px', background: 'rgba(0,0,0,.14)', borderLeft: '1px solid rgba(0,0,0,.2)' }}>
                          <span style={{ fontSize: 14 }}>🪙</span>
                          <span className="font-mono-brand font-black" style={{ fontSize: 12.5 }}>{(openCase.price * qty).toLocaleString()}</span>
                        </span>
                      )}
                    </button>
                  );
                })()}
                <button
                  onClick={() => start(openCase, true)}
                  disabled={spin || busy}
                  style={{ background: '#15151d', border: '1px solid #26263a', color: spin ? '#43434f' : '#c9c9d4', borderRadius: 11, padding: '14px 24px', cursor: spin ? 'not-allowed' : 'pointer' }}
                  className="font-black uppercase flex items-center gap-2"
                >
                  <span className="material-symbols-outlined" style={{ fontSize: 17 }}>play_circle</span>
                  <span style={{ fontSize: 12, letterSpacing: '.1em' }}>Demo</span>
                </button>
              </div>
              <div className="flex items-center gap-4 flex-wrap">
                <div>
                  <div className="font-mono-brand uppercase" style={{ fontSize: 10, letterSpacing: '.18em', color: '#6a6a78', marginBottom: 5 }}>
                    Qty
                  </div>
                  <div className="flex items-center" style={{ background: '#15151d', border: '1px solid #26263a', borderRadius: 11, padding: 4, gap: 2 }}>
                    {[1, 2, 3, 4].map((n) => (
                      <button
                        key={n}
                        onClick={() => changeQty(n)}
                        disabled={spin || busy}
                        style={{
                          width: 34,
                          height: 34,
                          borderRadius: 8,
                          border: 'none',
                          cursor: spin || busy ? 'not-allowed' : 'pointer',
                          background: qty === n ? 'linear-gradient(135deg,#ff6b5f,#ff3d31)' : 'transparent',
                          color: qty === n ? '#12060a' : '#8b8b9c',
                          // Still selectable (the demo works at any size), but
                          // dimmed when the balance can't cover that many.
                          opacity: user && qty !== n && openCase.price * n > bal ? 0.4 : 1,
                          transition: 'background .15s,color .15s',
                        }}
                        title={`${(openCase.price * n).toLocaleString()} points`}
                        className="font-mono-brand font-black"
                      >
                        {n}
                      </button>
                    ))}
                  </div>
                </div>
                <div style={{ width: 1, height: 34, background: '#24242e' }} />
                <div className="font-mono-brand" style={{ fontSize: 11, color: '#7d7d8c' }}>
                  Balance
                  <div style={{ color: '#F6BB42', fontWeight: 700, fontSize: 13, marginTop: 2 }}>🪙 {bal.toLocaleString()}</div>
                </div>
              </div>
              </div>

              <div className="font-mono-brand text-center hidden sm:block" style={{ fontSize: 11, color: '#6a6a78', marginTop: -6, marginBottom: 18, letterSpacing: '.04em' }}>
                Enter to open · 1–4 for quantity · Esc to close
              </div>

              <div style={{ borderTop: '1px solid #1c1c24', paddingTop: 16 }}>
                <div className="font-mono-brand uppercase font-bold" style={{ fontSize: 11, letterSpacing: '.2em', color: '#8b8b9c', marginBottom: 12 }}>
                  Drop odds
                </div>
                <div style={{ display: 'grid', gap: 10, gridTemplateColumns: 'repeat(auto-fill,minmax(190px,1fr))' }}>
                  {[...(openCase.drops || [])].sort((a, b) => a.odds - b.odds).map((d) => (
                    <div
                      key={d.id}
                      className="flex items-center gap-2.5"
                      style={{
                        background: 'linear-gradient(135deg,#15151d,#101016)',
                        border: `1px solid ${oc(d.odds)}3d`,
                        borderRadius: 99,
                        padding: '7px 14px 7px 7px',
                      }}
                    >
                      <div
                        style={{
                          width: 38,
                          height: 38,
                          borderRadius: '50%',
                          flexShrink: 0,
                          overflow: 'hidden',
                          background: `radial-gradient(circle,${oc(d.odds)}33,transparent 72%)`,
                          border: `1px solid ${oc(d.odds)}55`,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        {d.image_url ? (
                          <img src={mediaUrl(d.image_url)} alt={d.name} style={{ width: '78%', height: '78%', objectFit: 'contain' }} />
                        ) : (
                          <span className="material-symbols-outlined" style={{ color: oc(d.odds), fontSize: 18 }}>redeem</span>
                        )}
                      </div>
                      <div style={{ minWidth: 0, flex: 1 }}>
                        <div className="font-mono-brand font-bold" style={{ fontSize: 12, color: '#e6e6ee', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {d.name}
                        </div>
                        <div className="font-mono-brand font-black" style={{ fontSize: 11, color: oc(d.odds), marginTop: 2 }}>
                          {pc(d.odds)} chance
                          {d.points_value ? <span style={{ color: '#F6BB42' }}> · 🪙{d.points_value}</span> : ''}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {dialog && (
              <div className="fixed inset-0 z-[80] flex items-center justify-center p-5" style={{ background: 'rgba(0,0,0,.82)' }} onClick={() => setDialog(null)}>
                <div
                  onClick={(e) => e.stopPropagation()}
                  style={{ background: '#111118', border: '1px solid #24242e', borderRadius: 16, padding: 26, maxWidth: dialog === 'verify' ? 560 : 420, maxHeight: '88vh', overflowY: 'auto' }}
                  className="w-full dark-scroll"
                >
                  <div className="font-display-lg font-black text-white" style={{ fontSize: 17, marginBottom: 12 }}>
                    {dialog === 'howto' ? 'How to claim?' : 'Provably fair'}
                  </div>
                  {dialog === 'howto' ? (
                    <div style={{ fontSize: 12.5, color: '#888899', lineHeight: 1.7 }}>
                      <div style={{ color: '#fff', fontWeight: 700, marginBottom: 6 }}>
                        Point winnings are added to your balance automatically.
                      </div>
                      Site credit and physical prizes are credited within 48h once your account is linked. Otherwise
                      you'll be tagged on Discord with instructions.
                    </div>
                  ) : (
                    <FairPanel
                      user={user}
                      fair={fair}
                      mine={mine}
                      proofs={proofs}
                      seedInput={seedInput}
                      setSeedInput={setSeedInput}
                      busy={fairBusy}
                      error={fairError}
                      onRotate={rotateSeed}
                      onCheck={checkPull}
                      onLogin={login}
                    />
                  )}
                  <button
                    onClick={() => setDialog(null)}
                    style={{ background: '#16161e', border: '1px solid #26263a', color: '#c9c9d4', borderRadius: 9, padding: '11px 0', width: '100%', marginTop: 18, cursor: 'pointer', fontWeight: 800, fontSize: 12 }}
                  >
                    Close
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
